import MediaItemLpComplete from "@/assets/images/lp-complete.png";
import MediaItemTransition from "@/assets/images/transition.png";
import { MutationTree } from "vuex";
import { LPItemMetadata, Exposure, ExposureItemMedia, ExposureStatus, ExposureType, initState, LessonState } from "./state";
import { getSeconds } from "@/utils/convertDuration";
import { ExposureDetailType } from "@/views/teacher-class/components/lesson-plan/lesson-plan";
import { defineVuexName, MODULES } from "@/store/utils";
import { MIN_ZOOM_RATIO } from "@/utils/constant";

const mutations: MutationTree<LessonState> = {
  //setExposures
  [defineVuexName(MODULES.LESSON.COMMITS.SET_EXPOSURES)](s: LessonState, p: { exposures: Exposure[] }) {
    s.exposures = p.exposures.map((exposure) => {
      if (exposure.type === ExposureType.TRANSITION) {
        const mediaItem: ExposureItemMedia = {
          id: exposure.id,
          image: {
            url: MediaItemTransition,
            width: 1920,
            height: 1080,
          },
          exposureType: ExposureDetailType.TRANSITION_BLOCK,
        };
        exposure.items.push({
          id: exposure.id,
          name: exposure.name,
          media: [mediaItem],
        });
      }

      if (exposure.type === ExposureType.COMPLETE) {
        const mediaItem: ExposureItemMedia = {
          id: exposure.id,
          image: {
            url: MediaItemLpComplete,
            width: 1920,
            height: 1080,
          },
          exposureType: ExposureDetailType.LP_COMPLETE_BLOCK,
        };
        exposure.items.push({
          id: exposure.id,
          name: exposure.name,
          media: [mediaItem],
        });
      }
      return exposure;
    });
  },
  //setCurrentExposure
  [defineVuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE)](s: LessonState, p: { id: string; preventSelectFirstSlideAutomatically?: boolean }) {
    const totalExposures = s.exposures.length;
    const isAlternateMediaShown = getSeconds(s.playedTime) === getSeconds(s.totalTime);

    s.exposures.forEach((e, i) => {
      // find the matched exposure and set as current exposure
      if (e.id === p.id) {
        s.currentExposure = e;
        // re-assign nextExposure and previousExposure
        s.nextExposure =
          i < totalExposures - 1 // not the last exposure
            ? s.exposures[i + 1]
            : undefined;
        s.previousExposure =
          i > 0 // not the first exposure
            ? s.exposures[i - 1]
            : undefined;

        // found the matched exposure, then break the loop.
        return false;
      }
      return true;
    });

    // set the first media item to currentExposureItemMedia
    if (!p.preventSelectFirstSlideAutomatically) {
      if (
        s.currentExposure &&
        (s.currentExposure.items.length > 0 ||
          s.currentExposure.contentBlockItems.length > 0 ||
          s.currentExposure.teachingActivityBlockItems.length > 0 ||
          (isAlternateMediaShown && s.currentExposure.alternateMediaBlockItems.flat().length > 0))
      ) {
        const firstItemMediaList = [
          ...s.currentExposure.items,
          ...s.currentExposure.contentBlockItems,
          ...s.currentExposure.teachingActivityBlockItems,
          ...(isAlternateMediaShown ? s.currentExposure.alternateMediaBlockItems.flat() : []),
        ];
        s.currentExposureItemMedia = undefined;
        const firstItem = firstItemMediaList.find((item) => item.media.length);
        if (firstItem) {
          s.currentExposureItemMedia = firstItem.media[0];
        }
      }
    }
  },
  //setCurrentExposureItemMedia
  [defineVuexName(MODULES.LESSON.COMMITS.SET_CURRENT_EXPOSURE_ITEM_MEDIA)](s: LessonState, p: { id: string; exposureType?: ExposureDetailType }) {
    if (p.exposureType) {
      s.currentExposureType = p.exposureType;
    } else {
      s.currentExposureType = undefined;
    }
    if (p.id === s.currentExposureItemMedia?.id) return;
    if (!p.id) {
      s.currentExposureItemMedia = undefined;
      return;
    }
    if (!s.currentExposure) return;
    const isAlternateMediaShown = getSeconds(s.playedTime) === getSeconds(s.totalTime);
    const combinedItems = [
      ...s.currentExposure.items,
      ...s.currentExposure.contentBlockItems,
      ...s.currentExposure.teachingActivityBlockItems,
      ...(isAlternateMediaShown ? s.currentExposure.alternateMediaBlockItems.flat() : []),
    ];
    for (const item of combinedItems) {
      const matchItemMedia = item.media.find((m) => m.id === p.id);
      if (matchItemMedia) {
        s.currentExposureItemMedia = matchItemMedia;
        if (!p.exposureType && matchItemMedia.exposureType) {
          s.currentExposureType = matchItemMedia.exposureType;
        }
        if (s.currentExposureItemMedia) break;
      }
    }
  },
  //setExposureStatus
  [defineVuexName(MODULES.LESSON.COMMITS.SET_EXPOSURE_STATUS)](s: LessonState, p: { id: string; status: ExposureStatus }) {
    const exposure = s.exposures.find((e) => e.id === p.id);
    if (exposure) {
      if (p.status) {
        exposure.status = p.status;
      }
    }
  },
  //setTotalTime
  [defineVuexName(MODULES.LESSON.COMMITS.SET_TOTAL_TIME)](s: LessonState, payload: { time: string }) {
    s.totalTime = payload.time;
  },
  //setPlayedTime
  [defineVuexName(MODULES.LESSON.COMMITS.SET_PLAYED_TIME)](s: LessonState, payload: { time: string }) {
    s.playedTime = payload.time;
  },
  //setPreviousExposure
  [defineVuexName(MODULES.LESSON.COMMITS.SET_PREVIOUS_EXPOSURE)](s: LessonState, p: { id: string }) {
    const exposure = s.exposures.find((e) => e.id === p.id);
    s.previousExposure = exposure;
    if (s.previousExposure && s.previousExposure.items.length > 0) {
      s.previousExposureItemMedia = undefined;
      const firstItem = s.previousExposure.items[0];
      if (firstItem.media.length > 0) {
        s.previousExposureItemMedia = firstItem.media[0];
      }
    }
  },
  //endCurrentContent
  [defineVuexName(MODULES.LESSON.COMMITS.END_CURRENT_CONTENT)](s: LessonState) {
    s.currentExposure = undefined;
  },
  //setPreviousExposureItemMedia
  [defineVuexName(MODULES.LESSON.COMMITS.SET_PREVIOUS_EXPOSURE_ITEM_MEDIA)](s: LessonState, p: { id: string }) {
    if (!s.previousExposure) return;
    for (const item of s.previousExposure.items) {
      s.previousExposureItemMedia = item.media.find((m) => m.id === p.id);
      if (s.previousExposureItemMedia) break;
    }
  },
  //clearLessonData
  [defineVuexName(MODULES.LESSON.COMMITS.CLEAR_LESSON_DATA)](s: LessonState) {
    s.exposures = [];
    s.currentExposure = undefined;
    s.nextExposure = undefined;
    s.currentExposureItemMedia = undefined;
    s.nextExposureItemMedia = undefined;
    s.prevExposureItemMedia = undefined;
    s.totalTime = "";
    s.playedTime = "";
    s.previousExposure = undefined;
    s.previousExposureItemMedia = undefined;
  },
  //storeCacheImage
  [defineVuexName(MODULES.LESSON.COMMITS.STORE_CACHE_IMAGE)](
    s: LessonState,
    payload: { url: string; metadata: LPItemMetadata; base64String: string },
  ) {
    // checking if existing
    const existingCache = s.cropCache?.cacheValues.find(
      (cacheValue) => cacheValue.url === payload.url && JSON.stringify(cacheValue.metadata) === JSON.stringify(payload.metadata),
    );

    // exist!, skip
    if (existingCache) {
      return;
    }

    // store cache value
    s.cropCache?.cacheValues.push(payload);
  },
  //clearCacheImage
  [defineVuexName(MODULES.LESSON.COMMITS.CLEAR_CACHE_IMAGE)](s: LessonState) {
    // clear all cropped image cache
    s.cropCache?.cacheValues.splice(0, s.cropCache?.cacheValues.length);
  },
  //setZoomRatio
  [defineVuexName(MODULES.LESSON.COMMITS.SET_ZOOM_RATIO)](s: LessonState, p: number | undefined) {
    if (!p) return;
    s.zoomRatio = p;
  },
  //setImgCoords
  [defineVuexName(MODULES.LESSON.COMMITS.SET_IMG_COORDS)](s: LessonState, p: { x: number; y: number } | undefined) {
    s.imgCoords = p;
  },
  //setLessonPreviewObjects
  [defineVuexName(MODULES.LESSON.COMMITS.SET_LESSON_PREVIEW_OBJECTS)](s: LessonState, p: string) {
    s.previewObjects = p;
  },
  //setShowPreviewCanvas
  [defineVuexName(MODULES.LESSON.COMMITS.SET_SHOW_PREVIEW_CANVAS)](s: LessonState, p: boolean) {
    s.isShowPreviewCanvas = p;
  },
  //setAlternateMediaUrl
  [defineVuexName(MODULES.LESSON.COMMITS.SET_ALTERNATE_MEDIA_URL)](s: LessonState, p: { id: string; url: string }) {
    if (s.currentExposure?.alternateMediaBlockItems) {
      const alternateMedia = [...s.currentExposure?.alternateMediaBlockItems.flat()];
      const result = alternateMedia.find((item) => {
        return item.id === p.id;
      });
      if (result?.media[0]) result.media[0].image.url = p.url;
    }
  },
  //clearZoomState
  [defineVuexName(MODULES.LESSON.COMMITS.CLEAR_ZOOM_STATE)](s: LessonState) {
    s.zoomRatio = MIN_ZOOM_RATIO;
    s.imgCoords = { x: 0, y: 0 };
  },
  //resetState
  [defineVuexName(MODULES.LESSON.COMMITS.RESET_STATE)](s: LessonState) {
    Object.assign(s, initState());
  },
  //setPdfScrollProgress
  [defineVuexName(MODULES.LESSON.COMMITS.SET_PDF_SCROLL_PROGRESS)](s: LessonState, progress: number) {
    s.pdfScrollProgress = progress;
  },
  [defineVuexName(MODULES.LESSON.COMMITS.SET_VISIBLE_TARGET_TAGS)](s: LessonState, payload: string[]) {
    s.visibleTargetTags = payload;
  },
  [defineVuexName(MODULES.LESSON.COMMITS.CLEAR_EXPOSURE)](s: LessonState) {
    s.currentExposure = undefined;
    s.nextExposure = undefined;
    s.previousExposure = undefined;
    s.currentExposureItemMedia = undefined;
    s.nextExposureItemMedia = undefined;
    s.prevExposureItemMedia = undefined;
  },
};

export default mutations;
